import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { useCounters } from '../../contexts/CounterContext';
import { useToast } from '../../utils/toastUtils';
import ToastContainer from '../common/ToastContainer';
import CreateCounterModal from '../modals/CreateCounterModal';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

function CounterDeleted() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addCounter } = useCounters();
  const { toasts, showToast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const modalRef = useRef<Modal | null>(null);

  // Set document title
  useDocumentTitle(
    t('counterDeleted.title', 'Tap Counter Deleted'),
    'TapDonkey',
    t('counterDeleted.metaDescription', 'Tap counter deleted - TapDonkey')
  );

  const openCreateModal = () => {
    const modalEl = document.getElementById('createCounterModal');
    if (modalEl) {
      const modal = new Modal(modalEl);
      modalRef.current = modal;
      modal.show();
    }
  };

  const handleCreateCounter = async (name: string, isPrivate?: boolean, email?: string) => {
    try {
      setIsCreating(true);
      const newCounter = await addCounter(name, isPrivate, email);
      
      // Hide the modal
      if (modalRef.current) {
        modalRef.current.hide();
      }
      
      // Navigate to the new counter
      navigate(`/${newCounter.ownerUrl}`);
    } catch (err) {
      console.error('Failed to create counter:', err);
      showToast(t('errors.failedToCreate', 'Failed to create counter'), 'danger');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="container text-center py-5">
      <div className="card shadow p-5 mx-auto" style={{ maxWidth: "600px" }}>
        <div className="mb-4">
          <i className="bi bi-check-circle-fill text-success display-1"></i>
        </div>
        <h1 className="mb-3">{t('counterDeleted.heading', 'Tap Counter Deleted')}</h1>
        <p className="mb-4 lead">
          {t('counterDeleted.message', 'Your tap counter and all its data have been permanently removed.')}
        </p>
        <div className="d-grid">
          <button 
            className="btn btn-primary"
            onClick={openCreateModal}
          >
            <i className="bi bi-plus-circle me-2"></i>
            {t('counterDeleted.createNew', 'New Counter')}
          </button>
        </div>
      </div>

      {/* Create Counter Modal */}
      <CreateCounterModal 
        isCreating={isCreating} 
        onCreateCounter={handleCreateCounter}
      />
      
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} title={t('counterDeleted.title', 'Tap Counter Deleted')} />
    </div>
  );
}

export default CounterDeleted; 